import {AnalysisFinding, FindingSeverity} from '../types';

export const severityColor = (severity: FindingSeverity) =>
    severity === 'WARNING' ? 'yellow' : 'blue';

/**
 * Decision groups. The analyzer emits stable `key`s (see repository-analyzer.ts
 * in flexinet-devops-pipelines-api) — we bucket them by the question each
 * finding answers, so evidence sits next to the conclusion it justifies rather
 * than in an undifferentiated pile.
 */
export type DecisionGroupId = 'archetype' | 'build' | 'stages' | 'other';

export const DECISION_GROUP_LABELS: Record<DecisionGroupId, string> = {
    archetype: 'What kind of repository is this?',
    build: 'How does it build?',
    stages: 'What gets built per stage?',
    other: 'Other observations',
};

export const decisionGroupForKey = (key: string): DecisionGroupId => {
    if (key === 'archetype' || key.startsWith('archetype.')) return 'archetype';
    if (
        key === 'buildCommand' ||
        key.startsWith('framework.') ||
        key.startsWith('viteOutDir.') ||
        key.startsWith('vite.')
    ) return 'build';
    if (key.startsWith('stages.')) return 'stages';
    return 'other';
};

export interface DecisionGroup {
    id: DecisionGroupId;
    label: string;
    findings: AnalysisFinding[];
}

export const groupFindingsByDecision = (
    findings: AnalysisFinding[],
): DecisionGroup[] => {
    const order: DecisionGroupId[] = ['archetype', 'build', 'stages', 'other'];
    const buckets = new Map<DecisionGroupId, AnalysisFinding[]>();

    findings.forEach((finding) => {
        const id = decisionGroupForKey(finding.key);
        const existing = buckets.get(id);
        if (existing) {
            existing.push(finding);
        } else {
            buckets.set(id, [finding]);
        }
    });

    return order
        .filter((id) => buckets.has(id))
        .map((id) => ({
            id,
            label: DECISION_GROUP_LABELS[id],
            findings: buckets.get(id) as AnalysisFinding[],
        }));
};
